import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  BulkCollectionsRequest,
  Collection,
  CollectionProductItem,
  PatchAdminV1StoresStoreIdCollectionsIdBody,
  PostAdminV1StoresStoreIdCollectionsBody,
  ProductCollectionsResponse,
  ProductSelection,
} from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';

import { collectionsApi } from '../api/collections-api';

import { collectionKeys } from './collection-keys';

function remember(
  queryClient: ReturnType<typeof useQueryClient>,
  storeId: string,
  row: Collection,
) {
  queryClient.setQueryData(collectionKeys.detail(storeId, row.id), row);
  return queryClient.invalidateQueries({ queryKey: collectionKeys.lists(storeId) });
}

/** Field errors (handle taken, rules) are rendered by the form, not toasted. */
export function useCreateCollection(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PostAdminV1StoresStoreIdCollectionsBody) =>
      collectionsApi.create(storeId, body),
    meta: { successMessage: 'collections:toasts.created', silentError: true },
    onSuccess: (row) => remember(queryClient, storeId, row),
  });
}

/** New rules re-materialise an automated collection, so its product list refetches too. */
export function useUpdateCollection(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PatchAdminV1StoresStoreIdCollectionsIdBody }) =>
      collectionsApi.update(storeId, id, body),
    meta: { successMessage: 'collections:toasts.updated', silentError: true },
    onSuccess: (row) =>
      Promise.all([
        remember(queryClient, storeId, row),
        queryClient.invalidateQueries({ queryKey: collectionKeys.products(storeId, row.id) }),
      ]),
  });
}

export function useDeleteCollection(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => collectionsApi.remove(storeId, id),
    meta: { successMessage: 'collections:toasts.deleted', silentError: true },
    onSuccess: (_void, id) => {
      queryClient.removeQueries({ queryKey: collectionKeys.detail(storeId, id) });
      return queryClient.invalidateQueries({ queryKey: collectionKeys.lists(storeId) });
    },
  });
}

/** Replaces the manual list; the product count and every product's membership view change with it. */
export function useSetCollectionProducts(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, productIds }: { id: string; productIds: string[] }) =>
      collectionsApi.setProducts(storeId, id, productIds),
    meta: { successMessage: 'collections:toasts.productsSaved', silentError: true },
    onSuccess: (items, { id }) => {
      queryClient.setQueryData(collectionKeys.products(storeId, id), items);
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: collectionKeys.detail(storeId, id) }),
        queryClient.invalidateQueries({ queryKey: collectionKeys.lists(storeId) }),
        queryClient.invalidateQueries({ queryKey: collectionKeys.forProducts(storeId) }),
      ]);
    },
  });
}

export function useRefreshCollection(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => collectionsApi.refresh(storeId, id),
    meta: { successMessage: 'collections:toasts.refreshed' },
    onSuccess: (row) =>
      Promise.all([
        remember(queryClient, storeId, row),
        queryClient.invalidateQueries({ queryKey: collectionKeys.products(storeId, row.id) }),
      ]),
  });
}

/**
 * Joins or leaves manual collections from the product side; every collection's list may move.
 * A 409 `meta.published: false` means the product left the store meanwhile: the card flips to
 * its "publish first" state at once and refetches.
 */
export function useSetProductCollections(storeId: string, productId: string) {
  const queryClient = useQueryClient();
  const key = collectionKeys.forProduct(storeId, productId);
  return useMutation({
    mutationFn: (collectionIds: string[]) =>
      collectionsApi.setForProduct(storeId, productId, collectionIds),
    meta: { successMessage: 'collections:toasts.membershipSaved', silentError: true },
    onSuccess: (result) => {
      queryClient.setQueryData(key, result);
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: collectionKeys.lists(storeId) }),
        queryClient.invalidateQueries({ queryKey: collectionKeys.details(storeId) }),
      ]);
    },
    onError: (error) => {
      if (!isApiError(error) || error.code !== 'CONFLICT' || error.meta.published !== false) return;
      queryClient.setQueryData<ProductCollectionsResponse>(key, (current) =>
        current
          ? {
              published: false,
              items: current.items.map((i) => ({ ...i, member: false, matchedByRules: false })),
            }
          : current,
      );
      return queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

function useMembershipChanged(storeId: string) {
  const queryClient = useQueryClient();
  return (id: string) =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: collectionKeys.detail(storeId, id) }),
      queryClient.invalidateQueries({ queryKey: collectionKeys.lists(storeId) }),
      queryClient.invalidateQueries({ queryKey: collectionKeys.forProducts(storeId) }),
    ]);
}

/**
 * Appends to a manual list in one call: ticked ids (the collection's cap of 2,000 is also the
 * call's), or a selection of every matching store product the server resolves. The answer is
 * the whole list.
 */
export function useAddCollectionProducts(storeId: string) {
  const queryClient = useQueryClient();
  const changed = useMembershipChanged(storeId);
  return useMutation({
    mutationFn: ({ id, selection }: { id: string; selection: ProductSelection }) =>
      collectionsApi.addProducts(
        storeId,
        id,
        selection.mode === 'ids' ? { productIds: selection.ids } : { selection },
      ),
    meta: { successMessage: 'collections:toasts.productsAdded', silentError: true },
    onSuccess: (items, { id }) => {
      queryClient.setQueryData(collectionKeys.products(storeId, id), items);
      return changed(id);
    },
  });
}

export function useRemoveCollectionProduct(storeId: string) {
  const queryClient = useQueryClient();
  const changed = useMembershipChanged(storeId);
  return useMutation({
    mutationFn: ({ id, productId }: { id: string; productId: string }) =>
      collectionsApi.removeProduct(storeId, id, productId),
    meta: { successMessage: 'collections:toasts.productRemoved', silentError: true },
    onSuccess: (_void, { id, productId }) => {
      queryClient.setQueryData<CollectionProductItem[]>(
        collectionKeys.products(storeId, id),
        (current) =>
          current
            ?.filter((item) => item.productId !== productId)
            .map((item, position) => ({ ...item, position })),
      );
      return changed(id);
    },
  });
}

/** Status or delete for many collections; the caller shows the per-row result of a 207. */
export function useBulkCollections(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: BulkCollectionsRequest) => collectionsApi.bulk(storeId, body),
    meta: { silentError: true },
    onSuccess: (_result, body) => {
      if (body.action === 'delete') {
        for (const id of body.ids) {
          queryClient.removeQueries({ queryKey: collectionKeys.detail(storeId, id) });
        }
      }
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: collectionKeys.lists(storeId) }),
        queryClient.invalidateQueries({ queryKey: collectionKeys.details(storeId) }),
        queryClient.invalidateQueries({ queryKey: collectionKeys.forProducts(storeId) }),
      ]);
    },
  });
}
